import { reactive } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import axios from 'axios';
import { api } from '/app/plugins/services';
import { config } from '/app/config';
import { track } from '/app/utils/analytics';
import { videoFrameThumbnail } from '/app/utils/video-thumbnail';
import { useUploadPreviews } from '/app/composables/upload-previews';
import { useVideoProcessing } from '/app/composables/video-processing';

// Shared, app-level registry that OWNS the upload transport. The bytes-transfer
// used to live inside UploadFile.vue, so it died the moment the component
// unmounted (modal closed / navigated away). Moving it here — a scope pinned by
// app-init, exactly like useVideoProcessing — lets an upload keep running while the
// user works elsewhere. UploadFile is now a thin subscriber that enqueues a job
// and mirrors its progress.
//
// Job shape:
//   { id, label, type, size, progress, status, attachment, error, cancel, settled }
//   attachment: the Attachment delivered to hosts when the job succeeds
//   settled: promise resolving with the job once it reaches a terminal status
//            (never rejects — check job.status)
const API_BASE_URL = config.api.baseURL;
const MAX_JOBS = 50;

// LOST is never assigned to a live job — statusOf answers it for job ids that
// no longer resolve (e.g. after a reload emptied the in-memory queue).
export const UPLOAD_STATUS = {
    UPLOADING: 'uploading',
    SUCCESS: 'success',
    ERROR: 'error',
    CANCELLED: 'cancelled',
    LOST: 'lost',
};

// The common host subscription: run `fn` with the job's Attachment iff the
// upload succeeds. `settled` resolves with the terminal job and never rejects,
// so hosts don't need their own status guard.
export const onUploadSuccess = (job, fn) =>
    job.settled.then((settled) => {
        if (settled.status === UPLOAD_STATUS.SUCCESS) fn(settled.attachment);
    });

// Job ids must be unique across page loads, not merely within one: placeholders
// persist in drafts/localStorage keyed by uploadId, and the prune path matches
// them against live jobs — a reload-reset counter would let a stale placeholder
// collide with an unrelated new job and reconcile with the wrong Attachment.
const SESSION_TOKEN = Math.random().toString(36).slice(2, 10);
let seq = 0;
const nextId = () => `upload_${SESSION_TOKEN}_${++seq}`;

export const useUploadQueue = createSharedComposable(() => {
    const uploadPreviews = useUploadPreviews();
    const videoProcessing = useVideoProcessing();

    const jobs = reactive([]);

    const find = (id) => jobs.find((job) => job.id === id);

    // The supported way to read a job by id — owns the "job is gone" semantics.
    const statusOf = (id) => find(id)?.status ?? UPLOAD_STATUS.LOST;
    const progressOf = (id) => Math.round(Number(find(id)?.progress ?? 0));

    // Stash a temporary thumbnail so the freshly-uploaded card shows something
    // while the server thumbnail is still being generated. Image => the file
    // itself; video => a captured frame. Keyed by the same key the card uses.
    const cachePreview = (key, fileObj) => {
        if (!key || !fileObj) return;

        if (fileObj.type?.startsWith('image')) {
            uploadPreviews.set(key, URL.createObjectURL(fileObj));
        } else if (fileObj.type?.startsWith('video')) {
            videoFrameThumbnail(fileObj).then((url) => uploadPreviews.set(key, url)).catch(() => {});
        }
    };

    const runVideo = async (job, file) => {
        const data = await api.forum.attachVideo(file, {
            name: job.label,
            type: file?.type,
            private: true,
        }, {
            onUpdateProgress: (value) => { job.progress = Number(value); },
        });

        const url = `${API_BASE_URL}/content/attachments/video/${data.videoId}/iframe`;

        // attachVideo succeeded, so the Attachment exists server-side from here
        // on — registration and delivery must not be lost to a metadata failure;
        // dimensions/aspect are presentational extras.
        let metadata = null;
        try {
            ({ data: metadata } = await api.forum.getVideoInfo(data.videoId));
        } catch (err) {
            console.error(err);
        }

        const aspectRatio = metadata ? metadata.input.width / metadata.input.height : undefined;

        if (metadata) track('Video Upload Info Retrieved', { metadata, aspectRatio });

        // Enabling the MP4 download is a long server-side job (minutes to hours).
        // Hand it to the shared video-processing registry, which polls in the
        // background and survives reloads, then surface the attachment immediately.
        videoProcessing.register({
            key: data.videoId,
            label: job.label,
            type: file?.type,
        });

        cachePreview(data.videoId, file);

        return {
            id: data.id,
            key: data.videoId,
            name: job.label,
            type: file?.type,
            meta: {
                ...(metadata?.input ?? {}),
                ...(file?.size ? { size: file.size } : {}),
                aspectRatio,
                downloadEnabled: false,
            },
            url,
        };
    };

    const runFile = async (job, file, cancelToken) => {
        const data = await api.forum.attachFile(file, {
            name: job.label,
            type: file?.type,
        }, {
            onUpdateProgress: (value) => { job.progress = Number(value); },
            cancelToken,
        });

        const url = `${API_BASE_URL}/content/attachments/file/${data.fileKey}`;

        cachePreview(data.fileKey, file);

        return {
            id: data.id,
            key: data.fileKey,
            name: job.label,
            type: file?.type,
            ...(file?.size ? { meta: { size: file.size } } : {}),
            url,
        };
    };

    const run = async (job, file, cancelSource) => {
        track('File Upload Started', { label: job.label, type: file?.type });

        try {
            const attachment = file?.type?.startsWith('video')
                ? await runVideo(job, file)
                : await runFile(job, file, cancelSource.token);

            // tus (video) ignores the cancel token, so a cancelled job can still
            // reach this point when its transport finishes — it is already
            // terminal; don't resurrect it as SUCCESS.
            if (job.status !== UPLOAD_STATUS.UPLOADING) return;

            job.attachment = attachment;
            job.status = UPLOAD_STATUS.SUCCESS;

            track('File Upload Completed', { label: job.label, type: file?.type });
        } catch (err) {
            if (job.status !== UPLOAD_STATUS.UPLOADING) return;

            if (axios.isCancel?.(err)) {
                job.status = UPLOAD_STATUS.CANCELLED;
                track('File Upload Cancelled', { label: job.label, type: file?.type });
            } else {
                console.error(err);
                job.error = err;
                job.status = UPLOAD_STATUS.ERROR;
                track('File Upload Failed', { label: job.label, type: file?.type, error: err });
            }
        }
    };

    // Kick off an upload and return its reactive job. The transport runs in this
    // shared scope, so the caller can unmount without stopping it.
    const enqueue = ({ file, label }) => {
        // Per-job token so cancelling one upload never touches another.
        const cancelSource = axios.CancelToken.source();

        // Resolves with the terminal job — never rejects — so an unobserved
        // failure can't surface as an unhandled rejection.
        let settle;
        const settled = new Promise((resolve) => { settle = resolve; });

        const job = reactive({
            id: nextId(),
            label: label ?? null,
            type: file?.type ?? null,
            size: file?.size ?? null,
            progress: 0,
            status: UPLOAD_STATUS.UPLOADING,
            attachment: null,
            error: null,
            cancel: null,
            settled,
        });

        // tus (video) ignores the cancel token, so the CANCELLED transition
        // happens here, eagerly — run() then refuses to overwrite a terminal
        // status when the transport eventually finishes.
        job.cancel = () => {
            cancelSource.cancel();

            if (job.status === UPLOAD_STATUS.UPLOADING) {
                job.status = UPLOAD_STATUS.CANCELLED;
                track('File Upload Cancelled', { label: job.label, type: job.type });
            }
        };

        jobs.push(job);

        // Trim oldest settled jobs only — evicting an in-flight job would orphan
        // its transport: statusOf would answer LOST while bytes still move, and
        // its Attachment would be dropped on arrival.
        if (jobs.length > MAX_JOBS) {
            for (let i = 0; i < jobs.length && jobs.length > MAX_JOBS;) {
                if (jobs[i].status !== UPLOAD_STATUS.UPLOADING) jobs.splice(i, 1);
                else i += 1;
            }
        }

        run(job, file, cancelSource).finally(() => settle(job));

        return job;
    };

    const remove = (id) => {
        const idx = jobs.findIndex((job) => job.id === id);
        if (idx !== -1) jobs.splice(idx, 1);
    };

    return {
        jobs,
        find,
        statusOf,
        progressOf,
        enqueue,
        remove,
    };
});
